import React from 'react';
import styles from './styles.module.css';
import { Tooltip } from '@mui/material';
import BodyMeasurePreview from './BodyMeasurePreview';
import { getBodyFatSourceLabel } from '../../modules/bodyMeasures';
import { computeBmi, computeDelta, formatDelta, formatMeasure, getValue } from './bodyMeasurementsMath';
import { bodyFatRuler, weightRuler } from './bodyIndicatorRanges';
import { buildBmiIndicator, buildCompositionIndicator, buildRatioIndicators } from './bodyCalculatedIndicators';

/**
 * Indicadores da avaliação: peso, altura, IMC classificado, gordura corporal
 * (digitada e pelas dobras) e as relações cintura-quadril e cintura-estatura.
 * Só aparece o que tem valor. A variação contra a referência fica em destaque
 * (sem cor de juízo) e, quando há faixa usual, uma régua mostra onde o valor
 * caiu (`bodyIndicatorRanges.js`). `birthday` dá a idade na data da avaliação.
 */
export function buildIndicators(history, current, reference, variant, birthday) {
  const items = [];
  const subject = { variant, birthday };
  const bmi = computeBmi(history, current);

  const weight = getValue(current, 'weight');
  if (weight !== null) {
    items.push({
      key: 'weight',
      label: 'Peso',
      value: formatMeasure(weight, 'weight'),
      delta: reference ? formatDelta(computeDelta(weight, getValue(reference, 'weight')), 'weight') : null,
      ruler: weightRuler(weight, bmi ? bmi.height : null),
      note: null,
    });
  }

  const height = getValue(current, 'height');
  if (height !== null) {
    items.push({
      key: 'height',
      label: 'Altura',
      value: formatMeasure(height, 'height'),
      delta: reference ? formatDelta(computeDelta(height, getValue(reference, 'height')), 'height') : null,
      ruler: null,
      note: null,
    });
  }

  const bmiItem = buildBmiIndicator(bmi, reference ? computeBmi(history, reference) : null, current, subject);
  if (bmiItem) items.push(bmiItem);

  const bodyFat = getValue(current, 'body_fat');
  if (bodyFat !== null) {
    items.push({
      key: 'body_fat',
      label: 'Gordura corporal',
      value: formatMeasure(bodyFat, 'body_fat'),
      delta: reference ? formatDelta(computeDelta(bodyFat, getValue(reference, 'body_fat')), 'body_fat') : null,
      ruler: bodyFatRuler(bodyFat, variant),
      note: getBodyFatSourceLabel(current.bodyFatSource) || null,
    });
  }

  const composition = buildCompositionIndicator(current, reference, subject);
  if (composition) items.push(composition);

  return [...items, ...buildRatioIndicators(history, current, reference, subject)];
}

const IndicatorRuler = ({ ruler }) => (
  <div className={styles.ruler}>
    <div className={styles.rulerBar}>
      {ruler.segments.map((segment, index) => (
        <span
          key={index}
          className={`${styles.rulerBand} ${styles[`tone_${segment.tone}`]}`}
          style={{ width: `${segment.width}%` }}
          title={segment.label}
        />
      ))}
      <span className={styles.rulerMarker} style={{ left: `${ruler.position}%` }} />
    </div>
    <div className={styles.rulerTicks}>
      {ruler.ticks.map((tick) => (
        <span key={tick.value} style={{ left: `${tick.position}%` }}>{tick.text}</span>
      ))}
    </div>
    <span className={styles.rulerCaption}>
      {ruler.caption}
      <span className={styles.rulerSource}>{ruler.source}</span>
    </span>
  </div>
);

/**
 * Com `onSelect(key)`, cada cartão mostra a prévia do indicador ao passar o
 * mouse e abre o histórico ao clicar.
 */
const BodyIndicatorCards = ({ history, current, reference, variant, birthday, onSelect }) => {
  const items = buildIndicators(history, current, reference, variant, birthday);
  if (items.length === 0) return null;

  return (
    <div className={styles.tiles}>
      {items.map((item) => {
        // A gordura pelas dobras depende do cadastro do paciente: sem prévia nem histórico
        const select = onSelect && item.selectable !== false ? onSelect : null;
        return (
          <Tooltip
            key={item.key}
            title={select ? <BodyMeasurePreview history={history} measureKey={item.key} /> : ''}
            placement="left"
            enterDelay={300}
            componentsProps={{ tooltip: { className: styles.previewTip } }}
          >
            <div
              className={`${styles.tile} ${select ? styles.selectable : ''}`}
              role={select ? 'button' : undefined}
              tabIndex={select ? 0 : undefined}
              onClick={select ? () => select(item.key) : undefined}
              onKeyDown={select ? (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); select(item.key); } } : undefined}
            >
              <div className={styles.tileHead}>
                <span className={styles.tileLabel}>{item.label}</span>
                {item.delta && <span className={styles.deltaPill}>{item.delta}</span>}
              </div>
              <span className={styles.tileValue}>{item.value}</span>
              {item.ruler && <IndicatorRuler ruler={item.ruler} />}
              {item.note && <span className={styles.tileNote}>{item.note}</span>}
              {(item.details || []).map((detail) => <span key={detail} className={styles.tileNote}>{detail}</span>)}
            </div>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default BodyIndicatorCards;
